import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  ArrowRight,
  ShieldCheck,
  Zap,
  Building2,
  Home as HomeIcon,
  Factory,
  MapPin,
  BadgeCheck,
  Wrench,
  Camera,
} from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import CtaBand from "@/components/CtaBand";
import ReviewsSection from "@/components/ReviewsSection";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import MediaPlaceholder from "@/components/MediaPlaceholder";
import HeroBackdrop from "@/components/HeroBackdrop";
import { api, absUrl } from "@/lib/api";

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [projects, setProjects] = useState([]);

  const loadReviews = () => {
    setLoadingReviews(true);
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => setReviews([]))
      .finally(() => setLoadingReviews(false));
  };
  useEffect(loadReviews, []);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, []);

  return (
    <>
      <Seo path="/" />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0b0b0f]">
        <HeroBackdrop />
        <div className="container-kx relative py-20 sm:py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="kx-badge" data-testid="hero-badge">
              <Zap className="h-3.5 w-3.5" /> {SITE.license}
            </span>
            <h1 className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Kensmarc <span className="text-amber-400">Electric</span>
            </h1>
            <p className="mt-4 font-display text-xl font-semibold uppercase tracking-[0.2em] text-neutral-200 sm:text-2xl">
              Residential • Commercial • Industrial
            </p>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-neutral-300">
              Professional electrical solutions across Los Angeles County — plus CCTV and security camera
              installation — from a licensed California C-10 electrical contractor.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-a-quote"
                data-testid="hero-quote-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-4 text-base font-bold uppercase tracking-wide text-[#0A0A0C] transition-all hover:bg-amber-300 hover:shadow-xl hover:shadow-amber-400/25"
              >
                Request a Quote
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={SITE.phonePrimary.tel}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2e2e39] px-8 py-4 text-base font-bold text-white transition-colors hover:border-amber-400/60"
              >
                <Phone className="h-5 w-5 text-amber-400" /> Call {SITE.phonePrimary.display}
              </a>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-400">
              <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-amber-400" /> California C-10 Contractor</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-400" /> {SITE.serviceZip} + ~{SITE.radiusMiles} mi · {SITE.countyShort}</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-400" /> CCTV & Security</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* POSITIONING STRIP */}
      <section className="border-y border-[#1a1a22] bg-[#0d0d12]">
        <div className="container-kx grid grid-cols-2 divide-x divide-[#1a1a22] md:grid-cols-4">
          {[
            { Icon: HomeIcon, label: "Residential", sub: "Homes & remodels" },
            { Icon: Building2, label: "Commercial", sub: "Offices & retail" },
            { Icon: Factory, label: "Industrial", sub: "Facilities & power" },
            { Icon: ShieldCheck, label: "CCTV & Security", sub: "Camera installs" },
          ].map(({ Icon, label, sub }) => (
            <div key={label} className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <Icon className="h-7 w-7 text-amber-400" />
              <p className="font-display text-lg font-bold uppercase text-white">{label}</p>
              <p className="text-xs text-neutral-500">{sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad" id="services">
        <div className="container-kx">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <SectionHeading
              eyebrow="What we do"
              title="Electrical services, done right"
              subtitle="From panel upgrades to full commercial and industrial projects, Kensmarc Electric takes on the full range of electrical work."
            />
            <Link to="/services" className="link-underline hidden shrink-0 text-sm font-semibold text-amber-400 md:inline-flex md:items-center md:gap-1">
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.06 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="kx-card kx-card-hover group flex h-full flex-col p-6"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400 transition-colors group-hover:bg-amber-400 group-hover:text-[#0A0A0C]">
                    <s.Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold uppercase text-white">{s.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-400">{s.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-amber-400">
                    Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
          <Link to="/services" className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-amber-400 md:hidden">
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* WHY / VALUE */}
      <section className="border-y border-[#1a1a22] bg-[#0c0c11]">
        <div className="container-kx grid gap-12 py-16 lg:grid-cols-2 lg:py-24">
          <div>
            <SectionHeading eyebrow="Why Kensmarc" title="A licensed contractor who does it all" />
            <p className="mt-5 leading-relaxed text-neutral-400">
              Kensmarc Electric is owned and operated by electrical contractor {SITE.owner}. We take on
              residential, commercial, and industrial electrical work across Los Angeles County — and, because
              we're electricians first, we also handle CCTV and security camera installation with clean,
              professional wiring.
            </p>
            <ul className="mt-7 space-y-4">
              {[
                { Icon: BadgeCheck, t: "Licensed California C-10", d: "A licensed and insured C-10 electrical contractor serving Los Angeles County." },
                { Icon: Wrench, t: "Hands-on project experience", d: "Residential, commercial, and industrial electrical handled by an experienced contractor." },
                { Icon: ShieldCheck, t: "Electrical + security", d: "Camera and surveillance installs backed by proper electrical know-how." },
              ].map(({ Icon, t, d }) => (
                <li key={t} className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{t}</p>
                    <p className="text-sm text-neutral-400">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/about" className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-amber-400">
              About Kensmarc Electric <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <MediaPlaceholder slot="home-why" label="Project Photo Coming Soon" className="min-h-[320px]" testId="home-why-media-slot" />
        </div>
      </section>

      {/* RECENT WORK PREVIEW */}
      {projects.length > 0 && (
        <section className="section-pad" data-testid="home-recent-work">
          <div className="container-kx">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <SectionHeading
                eyebrow="Our Work"
                title="Recent projects"
                subtitle="Real Kensmarc Electric jobs — residential and commercial work across Los Angeles County."
              />
              <Link to="/our-work" className="link-underline hidden shrink-0 text-sm font-semibold text-amber-400 md:inline-flex md:items-center md:gap-1">
                View all work <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {projects.slice(0, 3).map((p, i) => {
                const cover = p.media?.[0];
                const src = cover?.type === "video" ? absUrl(cover.poster || cover.url) : absUrl(cover?.url);
                return (
                  <div key={p.id}>
                    <Link
                      to="/our-work"
                      data-testid={`home-project-${p.id.slice(0, 8)}`}
                      className="group relative block aspect-[4/3] overflow-hidden rounded-xl border border-[#22222c] bg-[#141419]"
                    >
                      {src && (
                        <img src={src} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                      <span className="absolute left-3 top-3 rounded-full bg-amber-400/90 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-wide text-[#0A0A0C]">
                        {p.category}
                      </span>
                      <div className="absolute inset-x-0 bottom-0 p-4">
                        {p.service && <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-amber-400">{p.service}</span>}
                        <h3 className="font-display text-lg font-bold uppercase text-white">{p.title}</h3>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
            <Link to="/our-work" className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-amber-400 md:hidden">
              View all work <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* CCTV TEASER */}
      <section className="section-pad">
        <div className="container-kx grid items-center gap-10 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <MediaPlaceholder slot="home-cctv" Icon={Camera} label="CCTV Project Photo Coming Soon" className="min-h-[300px]" testId="home-cctv-media-slot" />
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="Beyond electrical"
              title="CCTV & security camera installation"
              subtitle="Kensmarc Electric also installs surveillance and security cameras for homes and businesses — with clean wiring and dependable power from a licensed electrician."
            />
            <Link
              to="/cctv-security"
              data-testid="home-cctv-btn"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#2e2e39] px-6 py-3 text-sm font-bold text-white transition-colors hover:border-amber-400/60"
            >
              Explore CCTV & Security <ArrowRight className="h-4 w-4 text-amber-400" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE AREA TEASER */}
      <section className="border-y border-[#1a1a22] bg-[#0c0c11]">
        <div className="container-kx flex flex-col items-start justify-between gap-6 py-14 md:flex-row md:items-center">
          <div className="flex items-start gap-4">
            <MapPin className="mt-1 h-8 w-8 shrink-0 text-amber-400" />
            <div>
              <h3 className="font-display text-2xl font-bold uppercase text-white">Serving {SITE.serviceZip} & Los Angeles County</h3>
              <p className="mt-2 max-w-2xl text-neutral-400">{SITE.serviceAreaBlurb}</p>
            </div>
          </div>
          <Link to="/service-area" className="shrink-0 rounded-full border border-[#2e2e39] px-6 py-3 text-sm font-bold text-white transition-colors hover:border-amber-400/60">
            Check your area
          </Link>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-pad">
        <div className="container-kx">
          <SectionHeading eyebrow="Reviews" title="Customer feedback" />
          <div className="mt-8">
            <ReviewsSection reviews={reviews} loading={loadingReviews} onSubmitted={loadReviews} />
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
